/**
 * buildFileActions — the ONE source of truth for "which actions apply to this
 * file, in what order/grouping, with what label + icon", shared by the mobile
 * detail sheet, the drive-home project cards, and (since #437) the app shell
 * and workspace tab menus, so the type logic never forks. The desktop
 * drive-row menu still builds its own items but shares this module's
 * permission predicates.
 *
 * An action appears only when BOTH its type/permission condition holds AND the
 * host provided a handler for it. That's how one catalog serves two surfaces:
 * the mobile sheet wires a subset (no cut / metadata / debug-log), the desktop
 * menu wires the full set — each simply omits the handlers it doesn't support.
 *
 * Per-type behavior (the whole point):
 *   .zip                 → Extract here · Extract to…
 *   .project / .miniapp  → Browse as folder   (their tap opens workspace/app)
 *   .miniapp             → + Edit app          (open its workspace = workshop)
 *   plain file           → Open in new tab
 * plus the type-agnostic verbs (share, move, copy, rename, download, delete…).
 */
import { FolderOpen, Wrench, FileArchive, PackageOpen, ExternalLink, Share2, Users, Pencil, Copy, CopyPlus, FolderInput, Scissors, Download, Trash2, Info, Bug, Palette, SquarePlus, AppWindow as AppWindowIcon } from 'lucide-react'
import { presentsAsMiniapp, isMiniappFolder, isProjectFolder, isMountFileId, supportsDebugLog } from '@papan/types'
import { canShareMiniApp, type CrumbLike } from '@papan/share-links/client'
import type { FileItem } from './useFiles'
import { PDAppIcon, type GlyphIcon } from '@papan/core-ui/ui'

export type FileActionId =
  | 'browse' | 'editApp' | 'editIcon' | 'openAsApp' | 'extractHere' | 'extractTo' | 'openNewTab'
  | 'duplicate' | 'copyTo' | 'moveTo' | 'cut' | 'rename' | 'download'
  | 'shareLink' | 'shareMiniApp' | 'shareOrg' | 'shareInternally' | 'whoHasAccess'
  | 'details' | 'debugLog' | 'delete' | 'addToHomeScreen'

/** Coarse groups for spacing/dividers in the host UI (order is the array order). */
export type FileActionGroup = 'type' | 'organize' | 'share' | 'meta' | 'danger'

export interface FileActionItem {
  id: FileActionId
  label: string
  icon: GlyphIcon
  /** Optional accent class for the icon (e.g. a colored glyph). */
  iconClassName?: string
  group: FileActionGroup
  /** Marked for the mobile quick-action strip (Share · Move · Details · Delete). */
  primary?: boolean
  danger?: boolean
  /** Rendered greyed and non-interactive. Used when a gating input is still
   *  being fetched — an unresolved permission is NOT a denial (#437). */
  disabled?: boolean
  /** Opens a modal/picker — the mobile sheet closes instantly so it appears in place. */
  opensModal?: boolean
  /** Toggles an inline panel (Details) rather than navigating away. */
  inline?: boolean
  /** Fire the action (already bound to the file). */
  run: () => void
}

type Translate = (key: string, options?: Record<string, unknown>) => string
export type FileActionHandlers = Partial<Record<FileActionId, (f: FileItem) => void>>

export interface FileActionCtx {
  t: Translate
  isOrg: boolean
  canWrite: boolean
  /** Only the ids present here can appear — this is what scopes the surface. */
  handlers: FileActionHandlers
  /** The current folder's breadcrumb path — used to decide "Share PDApp"
   *  eligibility (an HTML file with a `.miniapp` ancestor). Absent ⇒ no offer. */
  miniappAncestors?: CrumbLike[]
  /** The caller's effective permission on this file. ABSENT means the caller
   *  did not supply one, and the coarse pre-#437 gating applies — that is what
   *  keeps MobileFileSheet and ProjectCard unchanged. Never treat absent as a
   *  denial. */
  permission?: string
  /** True while `permission` is in flight. Permission-dependent actions are
   *  emitted DISABLED rather than hidden: hiding would read as a denial, which
   *  is the `can()` bug that made Settings → Billing refuse an org's owner. */
  permissionPending?: boolean
}

/** Manage-access floor: the server resolves owner to `owner` on every row, so
 *  this is a permission test, NOT a role test (see DriveView's rowCanManageAccess). */
export function canManageAccessFor(permission: string | undefined): boolean {
  return permission === 'admin' || permission === 'owner'
}

/** Write floor, matching WorkspaceExplorer's debug-log gate and the server —
 *  the one definition lives in @papan/types (#942). */
import { canWriteFor } from '@papan/types'
export { canWriteFor }

export function buildFileActions(file: FileItem, ctx: FileActionCtx): FileActionItem[] {
  const { t, isOrg, canWrite, handlers, miniappAncestors = [], permission, permissionPending } = ctx
  // OPT-IN: a caller that supplies neither field gets the pre-#437 gating.
  const gated = permission !== undefined || permissionPending === true
  const mayManageAccess = !gated || permissionPending || canManageAccessFor(permission)
  const mayWriteFile = !gated || permissionPending || canWriteFor(permission)
  // presentsAsMiniapp is the RUN/BROWSE predicate — true for a real app folder
  // AND a `.shared`/`.chain` pointer row that presents as its target app.
  const isMiniapp = presentsAsMiniapp(file.name, file.fileType, file.isFolder)
  // AUTHORING (Edit app / Edit icon) needs a NATIVE app folder: the workspace +
  // icon APIs require a real `.miniapp` folder and reject a pointer id (or a
  // regular file), so those actions must gate on the folder, not presentation.
  // A nested app surfaced through a mount also arrives isFolder:true with its
  // real `.pdapp` name, but under a synthetic compound id (`s_<mount>_<src>`)
  // those same APIs can't load — exclude it too, or mobile offers a dead action.
  const isNativeMiniapp = !!file.isFolder && isMiniappFolder(file.name) && !isMountFileId(file.id)
  const isProject = isProjectFolder(file.name)
  const isPackage = isMiniapp || isProject
  const isZip = !file.isFolder && /\.zip$/i.test(file.name)

  const out: FileActionItem[] = []
  const add = (
    id: FileActionId,
    cond: boolean,
    meta: Omit<FileActionItem, 'id' | 'run'>,
  ) => {
    const fn = cond ? handlers[id] : undefined
    if (fn) out.push({ id, run: () => fn(file), ...meta })
  }

  // ── Type-specific ─────────────────────────────────────────────────
  add('editApp', isNativeMiniapp, { label: t('driveView.menu.editApp', { defaultValue: 'Edit app' }), icon: Wrench, group: 'type' })
  // Edit icon is a WRITE (server: requireMiniappManage — write on the .miniapp
  // folder + the modification safeguard), so hide it from read-only viewers
  // rather than let them click into a 403. Native folder only (rejects a pointer).
  add('editIcon', isNativeMiniapp && canWrite, { label: t('driveView.menu.editIcon', { defaultValue: 'Edit icon' }), icon: Palette, group: 'type', opensModal: true })
  add('browse', isPackage, { label: t('driveView.menu.browseAsFolder', { defaultValue: 'Browse as folder' }), icon: FolderOpen, group: 'type' })
  // Run a loose HTML page as a chromeless top-level app (Mode A). Mini-app
  // folders already run on tap, so this is only offered for HTML files — it's
  // their only run path (a plain tap opens the file viewer). Gated on a wired
  // handler like every action, so surfaces that don't support it just omit it.
  add('openAsApp', !file.isFolder && /\.html?$/i.test(file.name), { label: t('driveView.menu.openAsApp'), icon: AppWindowIcon, iconClassName: 'text-purple-500', group: 'type' })
  add('extractHere', isZip && canWrite, { label: t('driveView.menu.extractHere'), icon: FileArchive, group: 'type' })
  add('extractTo', isZip && canWrite, { label: t('driveView.menu.extractTo', { defaultValue: 'Extract to…' }), icon: PackageOpen, group: 'type', opensModal: true })
  add('openNewTab', !file.isFolder && !isPackage, { label: t('driveView.menu.openInNewTab'), icon: ExternalLink, group: 'type' })

  // ── Organize ──────────────────────────────────────────────────────
  // Copy-in-place (server auto-renames the copy) — no destination picker.
  add('duplicate', canWrite, { label: t('driveView.menu.duplicate', { defaultValue: 'Make a copy' }), icon: CopyPlus, group: 'organize' })
  add('copyTo', canWrite, { label: t('driveView.menu.copyTo', { defaultValue: 'Copy to…' }), icon: Copy, group: 'organize', opensModal: true })
  add('moveTo', canWrite, { label: t('driveView.menu.move', { defaultValue: 'Move to…' }), icon: FolderInput, group: 'organize', primary: true, opensModal: true })
  add('cut', canWrite, { label: t('driveView.menu.cut'), icon: Scissors, group: 'organize' })
  add('rename', canWrite, { label: t('driveView.menu.rename'), icon: Pencil, group: 'organize', opensModal: true })
  add('download', true, { label: file.isFolder ? t('driveView.menu.downloadAsZip') : t('driveView.menu.download'), icon: Download, group: 'organize' })

  // ── Share / access ────────────────────────────────────────────────
  // #218: save a plain folder as its own home-screen app. Only the iOS-Safari
  // mobile sheet supplies the handler (a full page load of the folder, then a
  // hint) — Safari reads the manifest once per page load, so this is how the
  // icon gets THIS folder rather than whatever page the tab loaded first.
  add('addToHomeScreen', file.isFolder && !isPackage, { label: t('driveView.menu.addToHomeScreen', { defaultValue: 'Add to Home Screen' }), icon: SquarePlus, group: 'share' })
  add('shareLink', true, { label: t('driveView.menu.shareLink'), icon: Share2, group: 'share', primary: true, opensModal: true })
  add('shareMiniApp', canShareMiniApp(file, miniappAncestors), { label: t('driveView.menu.shareMiniApp'), icon: PDAppIcon, iconClassName: 'text-pink-500', group: 'share', opensModal: true })
  add('shareOrg', isOrg, { label: t('driveView.menu.shareToDrive', { defaultValue: 'Share to another Drive' }), icon: Share2, group: 'share', opensModal: true })
  add('shareInternally', true, { label: t('driveView.shareInternally.action', { defaultValue: 'Share internally' }), icon: Users, group: 'share', opensModal: true })
  // Who has access is NOT gated on isOrg: a personal drive can still have share
  // links and cross-drive `.shared` recipients, so "who can see this?" is a real
  // question there. The dialog hides its People & teams tab on a personal drive
  // (hidePeopleTeams) — the caller passes that. Managers only (mayManageAccess);
  // on a personal drive the owner always qualifies.
  add('whoHasAccess', mayManageAccess, { label: t('driveView.menu.whoHasAccess'), icon: Users, group: 'share', opensModal: true, disabled: gated && permissionPending })

  // ── Meta ──────────────────────────────────────────────────────────
  add('details', true, { label: t('driveView.menu.details', { defaultValue: 'Details' }), icon: Info, group: 'meta', primary: true, inline: true })
  const mayDebugLog = !gated || permissionPending || (supportsDebugLog(file.name, !!file.isFolder) && mayWriteFile)
  add('debugLog', mayDebugLog, { label: t('driveView.menu.debugLog'), icon: Bug, group: 'meta', disabled: gated && permissionPending })

  // ── Danger ────────────────────────────────────────────────────────
  add('delete', canWrite, { label: t('driveView.menu.delete'), icon: Trash2, group: 'danger', primary: true, danger: true, opensModal: true })

  return out
}
